import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { CustomDeviceModel } from 'utils/globalModels';
import { FormInstance } from 'antd/es/form/Form';
import {
  initialAlertNotificationFormValues,
  AlertNotificationFormValues,
  AlertNotificationFormValuesNames,
} from 'functionalities/deviceAlertNotifications/components/AlertNotificationDrawer/config';
import { useMessage } from 'hooks/useMessage';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { GetAssetDetail } from 'core/domain/assets/repositories/getAssetDetail';
import { DefaultOptionType } from 'antd/lib/select';
import { UpdateAlertNotification } from 'core/domain/alertNotifications/repositories/updateAlertNotification';
import { CreateAlertNotification } from 'core/domain/alertNotifications/repositories/createAlertNotification';
import {
  AlertNotificationModel,
  AlertNotificationProjectUserModel,
  AlertNotificationUserModel,
} from 'core/domain/alertNotifications/models';
import { DeleteAlertNotification } from 'core/domain/alertNotifications/repositories/deleteAlertNotification';
import { GetAlertNotificationUserList } from 'core/domain/alertNotifications/repositories/getAlertNotificationUserList';
import { AssetProject } from 'models/assets.model';
import { DEFAULT_DELAY_SUCCESS_MESSAGE } from 'constants/time';

export interface AlertNotificationOptionsModel {
  device: CustomDeviceModel | null;
  form: FormInstance<AlertNotificationFormValues>;
  userList: DefaultOptionType[];
  alertNotificationLiterals: Record<string, string>;
  deleteAlertNotificationsModalLiterals: Record<string, string>;
  initialValues: AlertNotificationFormValues;
  onOpenAlertNotificationDrawer: (device: CustomDeviceModel) => void;
  onCloseAlertNotificationDrawer: () => void;
  onFinishForm: (values: AlertNotificationFormValues) => void;
  onSaveAlertNotification: () => void;
  onOpenDeleteAlertNotification: (device: CustomDeviceModel) => void;
  onCloseDeleteAlertNotification: () => void;
  onConfirmDeleteAlertNotification: () => void;
  onFinishFailed: () => void;
  isAlertNotificationDrawerVisible: boolean;
  isAlertNotificationDrawerLoading: boolean;
  isDeleteAlertNotificationModalVisible: boolean;
  isAlertNotificationProcessing: boolean;
}

export const useAlertNotification = () => {
  const { t } = useTranslation();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const [form] = Form.useForm();
  const { setMessageError, setMessageSuccess } = useMessage();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [device, setDevice] = useState<CustomDeviceModel | null>(null);
  const [userList, setUserList] = useState<DefaultOptionType[]>([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [successProcessingAlertNotification, setSuccessProcessingAlertNotification] = useState<boolean>(false);

  const literals: Record<string, string> = {
    title: device?.alertNotificationConfig ? t('_ALERT_NOTIFICATION_DRAWER_UPDATE_TITLE') : t('_ALERT_NOTIFICATION_DRAWER_CREATE_TITLE'),
    mainButtonText: t('save'),
    secondaryButtonText: t('cancel'),
    infoText: t('_ALERT_NOTIFICATION_DRAWER_INFO_MESSAGE'),
    enabledText: t('_ALERT_NOTIFICATION_DRAWER_USER_ENABLED_MESSAGE'),
    userListLabel: t('_ALERT_NOTIFICATION_DRAWER_USER_LIST_MESSAGE'),
    userListPlaceholder: t('_ALERT_NOTIFICATION_DRAWER_USER_LIST_PLACEHOLDER'),
  };

  const deleteModalLiterals: Record<string, string> = {
    title: t('_DELETE_ALERT_NOTIFICATION_MODAL_TITLE'),
    description: t('_DELETE_ALERT_NOTIFICATION_MODAL_DESCRIPTION'),
    mainButtonText: t('delete'),
    secondaryButtonText: t('cancel'),
  };

  useEffect(() => {
    isVisible && !!device && getData(device);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const getProjectId = async (assetId: string | null): Promise<string> => {
    try {
      const assetData = await GetAssetDetail(assetId || '');
      const projectId = (assetData.data.projects[0] as AssetProject).uuid;
      return projectId;
    } catch (error) {
      setMessageError({ description: t('_ALERT_NOTIFICATION_DRAWER_GETTING_DATA_ERROR_MESSAGE') });
      return '';
    }
  };

  const getData = async (currentDevice: CustomDeviceModel) => {
    setIsLoading(true);
    try {
      const { assetId, projectId } = currentDevice;
      const selectedProjectId: string = projectId || (await getProjectId(assetId));
      const userListResponse = await GetAlertNotificationUserList({ host, token, projectId: selectedProjectId });
      const { userList: currentUserList } = userListResponse.data;
      const parsedUserList = parseUserList(currentUserList);
      setUserList(parsedUserList);
      setAlertNotificationFormValues({ currentDevice, totalUserList: parsedUserList });
    } catch (error) {
      setMessageError({ description: t('_ALERT_NOTIFICATION_DRAWER_GETTING_DATA_ERROR_MESSAGE') });
    } finally {
      setIsLoading(false);
    }
  };

  const parseUserList = (currentUserList: AlertNotificationProjectUserModel[]): DefaultOptionType[] => {
    return currentUserList.map((user) => ({ label: `${user.username} (${user.email})`, value: user.id }));
  };

  const setAlertNotificationFormValues = ({
    currentDevice,
    totalUserList,
  }: {
    currentDevice: CustomDeviceModel;
    totalUserList: DefaultOptionType[];
  }) => {
    const parsedSelectedUserList: DefaultOptionType[] = currentDevice.alertNotificationUserList.map((userId) => {
      const selectedUser = totalUserList.find((selected) => selected.value === userId);
      return selectedUser || { label: '', value: userId };
    });

    form.setFieldsValue({
      [AlertNotificationFormValuesNames.ENABLED]: currentDevice.alertNotificationEnabled,
      [AlertNotificationFormValuesNames.USER_LIST]: parsedSelectedUserList,
    });
  };

  const onOpenAlertNotificationDrawer = (selectedDevice: CustomDeviceModel) => {
    setIsVisible(true);
    setDevice(selectedDevice);
  };

  const onReset = () => {
    setIsVisible(false);
    setDevice(null);
    setUserList([]);
  };

  const onCloseAlertNotificationDrawer = () => {
    onReset();
  };

  const parseUserListToBeSent = (values: Record<string, string>[] | string[]): AlertNotificationUserModel[] => {
    if (values.length === 0) {
      return [] as AlertNotificationUserModel[];
    }

    return values.map((value) => {
      const isValueStrings = typeof value === 'string';
      return { id: isValueStrings ? value : value.value };
    });
  };

  const onFinishForm = async (values: AlertNotificationFormValues) => {
    setIsProcessing(true);

    const parsedUserList = parseUserListToBeSent(values[AlertNotificationFormValuesNames.USER_LIST]);
    const data: AlertNotificationModel = {
      deviceUuid: device?.uuid || '',
      enabled: values[AlertNotificationFormValuesNames.ENABLED],
      userList: parsedUserList,
    };

    try {
      !!device?.alertNotificationConfig
        ? await UpdateAlertNotification({ token, host, data })
        : await CreateAlertNotification({ token, host, data });
      setMessageSuccess({ description: t('_ALERT_NOTIFICATION_PROCESS_SUCCESS_MESSAGE') });
      onReset();
      setSuccessProcessingAlertNotification(true);
    } catch (error) {
      setMessageError({ description: t('_ALERT_NOTIFICATION_PROCESS_ERROR_MESSAGE') });
    } finally {
      setIsProcessing(false);
      setTimeout(() => {
        setSuccessProcessingAlertNotification(false);
      }, DEFAULT_DELAY_SUCCESS_MESSAGE);
    }
  };

  const onSaveAlertNotification = () => {
    form.submit();
  };

  const onDeleteAlertNotification = (currentDevice: CustomDeviceModel) => {
    setDevice(currentDevice);
    setIsDeleteModalVisible(true);
  };

  const onConfirmDeleteAlertNotification = async () => {
    setIsProcessing(true);
    try {
      await DeleteAlertNotification({ token, host, deviceUuid: device?.uuid || '' });
      setMessageSuccess({ description: t('_DELETE_ALERT_NOTIFICATION_SUCCESS_MESSAGE') });
      setIsDeleteModalVisible(false);
      setSuccessProcessingAlertNotification(true);
    } catch (error) {
      setMessageError({ description: t('_DELETE_ALERT_NOTIFICATION_ERROR_MESSAGE') });
    } finally {
      onReset();
      setIsProcessing(false);
      setTimeout(() => {
        setSuccessProcessingAlertNotification(false);
      }, DEFAULT_DELAY_SUCCESS_MESSAGE);
    }
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalVisible(false);
  };

  const onFinishFailed = () => {
    setIsProcessing(false);
  };

  const alertNotificationOptions: AlertNotificationOptionsModel = {
    device,
    form,
    alertNotificationLiterals: literals,
    deleteAlertNotificationsModalLiterals: deleteModalLiterals,
    userList,
    initialValues: initialAlertNotificationFormValues,
    onOpenAlertNotificationDrawer,
    onCloseAlertNotificationDrawer,
    onFinishForm,
    onSaveAlertNotification,
    onOpenDeleteAlertNotification: onDeleteAlertNotification,
    onCloseDeleteAlertNotification: onCloseDeleteModal,
    onConfirmDeleteAlertNotification,
    onFinishFailed,
    isAlertNotificationDrawerVisible: isVisible,
    isAlertNotificationDrawerLoading: isLoading,
    isDeleteAlertNotificationModalVisible: isDeleteModalVisible,
    isAlertNotificationProcessing: isProcessing,
  };

  return {
    alertNotificationOptions,
    successProcessingAlertNotification,
  };
};
